import type { VisibilityState } from "@tanstack/react-table";

/** Column ids for portfolio positions table (must match backend allow-list). */
export const PORTFOLIO_POSITIONS_STABLE_PREFIX = ["asset_name", "actions"] as const;

export const PORTFOLIO_POSITIONS_DEFAULT_ORDER: string[] = [
  "asset_name",
  "actions",
  "last_price",
  "asset_quote_daily_change_pct",
  "daily_pnl_base",
  "daily_return_pct",
  "weight_pct",
  "current_value",
  "performance_absolute",
  "performance_percentage",
  "performance_absolute_native",
  "performance_percentage_native",
  "shares_owned",
  "avg_cost_basis",
];

/** Columns that must stay visible (still reorderable except fixed prefix). */
export const PORTFOLIO_POSITIONS_ALWAYS_VISIBLE = new Set([
  "asset_name",
  "actions",
  "current_value",
  "performance_absolute",
  "performance_percentage",
]);

const ALLOWED = new Set(PORTFOLIO_POSITIONS_DEFAULT_ORDER);

export const PORTFOLIO_POSITIONS_DEFAULT_VISIBILITY: VisibilityState = {
  last_price: true,
  daily_pnl_base: true,
  daily_return_pct: true,
  asset_quote_daily_change_pct: false,
  weight_pct: false,
  performance_absolute_native: false,
  performance_percentage_native: false,
  shares_owned: false,
  avg_cost_basis: false,
};

export const PORTFOLIO_POSITIONS_COLUMN_LABELS: Record<string, string> = {
  asset_name: "Asset",
  actions: "Trades",
  last_price: "Last price",
  asset_quote_daily_change_pct: "Day (quote)",
  daily_pnl_base: "Daily P&L",
  daily_return_pct: "Daily %",
  weight_pct: "Weight",
  current_value: "Value",
  performance_absolute: "Net P&L",
  performance_percentage: "Net Return",
  performance_absolute_native: "P&L (asset currency)",
  performance_percentage_native: "Return (asset currency)",
  shares_owned: "Quantity",
  avg_cost_basis: "Average cost",
};

export function getColumnIdFromDef(def: { id?: string; accessorKey?: unknown }): string {
  if (def.id) return def.id;
  if (typeof def.accessorKey === "string") return def.accessorKey;
  return "";
}

export function normalizePortfolioPositionsOrder(userOrder: string[]): string[] {
  const seen = new Set<string>();
  const rest: string[] = [];
  for (const id of userOrder) {
    if (!ALLOWED.has(id)) continue;
    if (PORTFOLIO_POSITIONS_STABLE_PREFIX.includes(id as "asset_name" | "actions")) {
      continue;
    }
    if (seen.has(id)) continue;
    seen.add(id);
    rest.push(id);
  }
  for (const id of PORTFOLIO_POSITIONS_DEFAULT_ORDER) {
    if (PORTFOLIO_POSITIONS_STABLE_PREFIX.includes(id as "asset_name" | "actions")) {
      continue;
    }
    if (!seen.has(id)) {
      rest.push(id);
      seen.add(id);
    }
  }
  return [...PORTFOLIO_POSITIONS_STABLE_PREFIX, ...rest];
}

export function normalizePortfolioPositionsVisibility(
  vis: VisibilityState,
): VisibilityState {
  const out: VisibilityState = { ...PORTFOLIO_POSITIONS_DEFAULT_VISIBILITY };
  for (const [k, v] of Object.entries(vis)) {
    if (!ALLOWED.has(k)) continue;
    if (PORTFOLIO_POSITIONS_ALWAYS_VISIBLE.has(k)) {
      continue;
    }
    out[k] = !!v;
  }
  return out;
}

export function mergeVisibilityWithDefaults(
  saved: VisibilityState | null | undefined,
): VisibilityState {
  if (!saved) return { ...PORTFOLIO_POSITIONS_DEFAULT_VISIBILITY };
  return normalizePortfolioPositionsVisibility(saved);
}

export interface PortfolioPositionsColumnConfigItem {
  id: string;
  label: string;
  canHide: boolean;
  canReorder: boolean;
}

export function getPortfolioPositionsModalColumnConfig(): PortfolioPositionsColumnConfigItem[] {
  return PORTFOLIO_POSITIONS_DEFAULT_ORDER.map((id) => ({
    id,
    label: PORTFOLIO_POSITIONS_COLUMN_LABELS[id] ?? id,
    canHide: !PORTFOLIO_POSITIONS_ALWAYS_VISIBLE.has(id),
    canReorder: !PORTFOLIO_POSITIONS_STABLE_PREFIX.includes(
      id as (typeof PORTFOLIO_POSITIONS_STABLE_PREFIX)[number],
    ),
  }));
}

